import PropTypes from "prop-types"
import cx from "classnames"
import { UserInfos } from "components/UserInfos"
import * as ReviewTarget from "enums/ReviewTarget"
import { Text } from "components/Text"
import { useTranslator } from "components/Translator"
import { Card, CardContent } from "components/Card"
import { Rating } from "components/Rating"
import { DescriptionsList, DescriptionItem } from "components/DescriptionsList"
import { Route } from "components/Route"
import {
  Accordion,
  AccordionContent,
  AccordionSummary,
} from "components/Accordion"
import { Separator } from "components/Separator"
import { AngleRight } from "components/Icon/AngleRight"
import { RawLink } from "components/Link"
import { useRouter } from "components/Router"
import { Button } from "components/Button"
import { Tag } from "components/Tag"
import { ConfirmButton } from "components/ConfirmButton"
import { TrashAlt } from "components/Icon"

export function ReviewCard(props) {
  const { review, target } = props
  const router = useRouter()
  const translator = useTranslator()
  const href =
    target === ReviewTarget.RECEIVED
      ? review.valuer?.nbReviews && review.valuer?.nbReviews > 0
        ? router.generate("app_account_review_index", {
            userId: review.valuer.id,
          })
        : router.generate("app_account_profile_show", {
            userId: review.valuer.id,
          })
      : review.user?.nbReviews && review.user?.nbReviews > 0
        ? router.generate("app_account_review_index", {
            userId: review.user.id,
          })
        : router.generate("app_account_profile_show", {
            userId: review.user.id,
          })

  return (
    <Card spacing="small">
      <CardContent className="bg-primary-lighter">
        <RawLink className="flex flex-row justify-between" href={href}>
          <UserInfos
            user={
              target === ReviewTarget.RECEIVED ? review.valuer : review.user
            }
            variant="compact"
          />
          <AngleRight className="w-3 text-primary-default" />
        </RawLink>
      </CardContent>
      <CardContent className="flex flex-col space-y-6">
        <div className="flex flex-col space-y-2">
          <div className="flex gap-x-2">
            <div className="grow">
              <Text className="text-light" variant="caption">
                {review.sailedAt
                  ? new Date(review.sailedAt).toLocaleDateString(
                      translator.locale,
                    ) + " • "
                  : null}
                {"#"}
                {review.id}
                {review.nbDays
                  ? " • " +
                    review.nbDays +
                    " " +
                    translator.trans("ReviewCard.nbDays", null, "components")
                  : null}
                {review.position ? " • " + review.position.enum_label : null}
              </Text>
            </div>

            {!review.finished ? (
              <Tag
                label={translator.trans(
                  "ReviewCard.ongoingDraft",
                  null,
                  "components",
                )}
                color="error"
              />
            ) : null}

            {!review.published && review.finished && review.moderatedOngoing ? (
              <Tag
                label={translator.trans(
                  "ReviewCard.ongoingNegative",
                  null,
                  "components",
                )}
                color="error"
              />
            ) : null}
          </div>

          {review.notSailed ? (
            <Text variant="headline6">
              {translator.trans("ReviewCard.notSailed", null, "components")} -{" "}
              {review.notSailedCause?.enum_label.split("(")[0]}
            </Text>
          ) : (
            <Rating value={Number(review.rating.enum_value)} />
          )}
          <Text variant="body1">{review.comment}</Text>
          <div className={cx("flex flex-col space-y-2 justify-center")}>
            <Text variant="caption" className="text-light">
              {translator.trans(
                review.published ? "ReviewCard.date" : "ReviewCard.draftDate",
                {
                  date: new Date(
                    review.published ? review.publishedAt : review.createdAt,
                  ).toLocaleDateString(translator.locale),
                },
                "components",
              )}
            </Text>

            <div className="flex gap-x-2">
              {!review.finished ? (
                <div className="grow">
                  <Button
                    href={router.generate("app_user_review_edit", {
                      reviewId: review.id,
                      step: 2,
                    })}
                    color="primary"
                    variant="text"
                    fullWidth
                  >
                    {translator.trans(
                      "ReviewCard.draftEdit",
                      null,
                      "components",
                    )}
                  </Button>
                </div>
              ) : null}

              {!review.finished ? (
                <ConfirmButton
                  color="danger"
                  variant="text"
                  confirmTitle={translator.trans(
                    "ReviewCard.delete.confirmTitle",
                    null,
                    "components",
                  )}
                  confirmContent={translator.trans(
                    "ReviewCard.delete.confirmContent",
                    null,
                    "components",
                  )}
                  confirmLabel={translator.trans(
                    "ReviewCard.delete.confirmLabel",
                    null,
                    "components",
                  )}
                  iconLeft={<TrashAlt className="w-4" />}
                  onConfirm={() =>
                    router.redirect(
                      router.generate("app_user_review_delete", {
                        reviewId: review.id,
                      }),
                    )
                  }
                />
              ) : null}
            </div>
          </div>
        </div>
        {review.technicalInfos ? (
          <div className="space-y-9 pb-9">
            <Separator />
            <Accordion>
              <AccordionSummary>
                <Text className="text-primary-dark" variant="headline5">
                  {translator.trans(
                    "ReviewCard.technicalInfos.cta",
                    null,
                    "components",
                  )}
                </Text>
              </AccordionSummary>
              <AccordionContent>
                {(expanded) =>
                  expanded ? (
                    <BoatCrewReviewInfos
                      boat={review.boat}
                      crew={review.crew}
                    />
                  ) : null
                }
              </AccordionContent>
            </Accordion>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

ReviewCard.propTypes = {
  review: PropTypes.shape({
    boat: PropTypes.object,
    comment: PropTypes.string,
    crew: PropTypes.object,
    id: PropTypes.number,
    nbDays: PropTypes.number,
    position: PropTypes.object,
    rating: PropTypes.object,
    sailedAt: PropTypes.string,
    publishedAt: PropTypes.string,
    published: PropTypes.bool,
    technicalInfos: PropTypes.bool,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    user: PropTypes.object,
    valuer: PropTypes.object,
  }).isRequired,
}

const BoatCrewReviewInfos = (props) => {
  const { boat, crew } = props
  const translator = useTranslator()

  return (
    <div className="space-y-7">
      {boat ? (
        <DescriptionsList>
          {boat.name ? (
            <DescriptionItem
              label={translator.trans(
                "ReviewCard.technicalInfos.modal.boatName",
                null,
                "components",
              )}
              value={boat.name}
            />
          ) : null}
          {boat.family ? (
            <DescriptionItem
              label={translator.trans(
                "ReviewCard.technicalInfos.modal.boatType",
                null,
                "components",
              )}
              value={boat.family}
            />
          ) : null}
          {crew.nbHours ? (
            <DescriptionItem
              label={translator.trans(
                "ReviewCard.technicalInfos.modal.nbHours",
                null,
                "components",
              )}
              value={crew.nbHours}
            />
          ) : null}

          {crew.nbMiles ? (
            <DescriptionItem
              label={translator.trans(
                "ReviewCard.technicalInfos.modal.nbMiles",
                null,
                "components",
              )}
              value={translator.trans(
                "ReviewCard.technicalInfos.modal.nbMilesValue",
                { nbMiles: crew.nbMiles },
                "components",
              )}
            />
          ) : null}
        </DescriptionsList>
      ) : null}

      {crew &&
      (crew.departurePortAddress?.formatted ||
        crew.arrivalPortAddress?.formatted) ? (
        <Route
          steps={[
            { place: crew.departurePortAddress?.formatted },
            { place: crew.arrivalPortAddress?.formatted },
          ]}
        />
      ) : null}

      {crew ? (
        <DescriptionsList>
          {crew.spiNavigation ? (
            <DescriptionItem
              label={translator.trans(
                "ReviewCard.technicalInfos.modal.spiNavigation",
                null,
                "components",
              )}
              value={crew.spiNavigation}
            />
          ) : null}

          {crew.role ? (
            <DescriptionItem
              label={translator.trans(
                "ReviewCard.technicalInfos.modal.role",
                null,
                "components",
              )}
              value={crew.role}
            />
          ) : null}
          {crew.shelterDistance ? (
            <DescriptionItem
              label={translator.trans(
                "ReviewCard.technicalInfos.modal.shelterDistance",
                null,
                "components",
              )}
              value={translator.trans(
                "ReviewCard.technicalInfos.modal.shelterDistanceValue",
                { shelterDistance: crew.shelterDistance },
                "components",
              )}
            />
          ) : null}

          {crew.windForce ? (
            <DescriptionItem
              label={translator.trans(
                "ReviewCard.technicalInfos.modal.windForce",
                null,
                "components",
              )}
              value={translator.trans(
                "ReviewCard.technicalInfos.modal.windForceValue",
                { windForce: crew.windForce },
                "components",
              )}
            />
          ) : null}

          {crew.nbNights ? (
            <DescriptionItem
              label={translator.trans(
                "ReviewCard.technicalInfos.modal.nbNights",
                null,
                "components",
              )}
              value={crew.nbNights}
            />
          ) : null}

          {crew.nbCrewMembers ? (
            <DescriptionItem
              label={translator.trans(
                "ReviewCard.technicalInfos.modal.nbCrewMembers",
                null,
                "components",
              )}
              value={crew.nbCrewMembers}
            />
          ) : null}

          {crew.comment ? (
            <DescriptionItem
              label={translator.trans(
                "ReviewCard.technicalInfos.modal.comment",
                null,
                "components",
              )}
              direction="column"
              value={crew.comment}
            />
          ) : null}
        </DescriptionsList>
      ) : null}
    </div>
  )
}
